// Music theory + chord-shape helpers, ported from poc/web/app.js (logic-identical, pure).

export const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
// Standard guitar tuning: string 1 (high E) .. string 6 (low E).
export const OPEN_MIDI: Record<number, number> = { 1: 64, 2: 59, 3: 55, 4: 50, 5: 45, 6: 40 };
export const MAKER_FRETS = 21;

const E_PC = 4;
const A_PC = 9;

export function midiName(midi: number): string {
  return `${NOTE_NAMES[midi % 12]}${Math.floor(midi / 12) - 1}`;
}

// Familiar open-position voicings; movable shapes cover the rest. frets [s6..s1], -1=muted, 0=open.
export const OPEN_CHORDS: Record<string, number[]> = {
  C: [-1, 3, 2, 0, 1, 0], D: [-1, -1, 0, 2, 3, 2], Dm: [-1, -1, 0, 2, 3, 1],
  E: [0, 2, 2, 1, 0, 0], Em: [0, 2, 2, 0, 0, 0], F: [1, 3, 3, 2, 1, 1],
  G: [3, 2, 0, 0, 0, 3], A: [-1, 0, 2, 2, 2, 0], Am: [-1, 0, 2, 2, 1, 0],
  G7: [3, 2, 0, 0, 0, 1], C7: [-1, 3, 2, 3, 1, 0], D7: [-1, -1, 0, 2, 1, 2],
  E7: [0, 2, 0, 1, 0, 0], A7: [-1, 0, 2, 0, 2, 0], B7: [-1, 2, 1, 2, 0, 2],
  Em7: [0, 2, 0, 0, 0, 0], Am7: [-1, 0, 2, 0, 1, 0], Dm7: [-1, -1, 0, 2, 1, 1],
  Cmaj7: [-1, 3, 2, 0, 0, 0], Fmaj7: [-1, -1, 3, 2, 1, 0], Gmaj7: [3, 2, 0, 0, 0, 2],
  Amaj7: [-1, 0, 2, 1, 2, 0], Dmaj7: [-1, -1, 0, 2, 2, 2], Emaj7: [0, 2, 1, 1, 0, 0],
};

const ROOT6: Record<string, number[]> = {
  "": [0, 2, 2, 1, 0, 0], m: [0, 2, 2, 0, 0, 0], 7: [0, 2, 0, 1, 0, 0],
  m7: [0, 2, 0, 0, 0, 0], maj7: [0, 2, 1, 1, 0, 0], 6: [0, 2, 2, 1, 2, 0],
  m6: [0, 2, 2, 0, 2, 0], mM7: [0, 2, 1, 0, 0, 0], sus4: [0, 2, 2, 2, 0, 0],
  aug: [0, 3, 2, 1, 1, 0],
};
const ROOT5: Record<string, number[]> = {
  "": [-1, 0, 2, 2, 2, 0], m: [-1, 0, 2, 2, 1, 0], 7: [-1, 0, 2, 0, 2, 0],
  m7: [-1, 0, 2, 0, 1, 0], maj7: [-1, 0, 2, 1, 2, 0], 6: [-1, 0, 2, 2, 2, 2],
  m6: [-1, 0, 2, 2, 1, 2], mM7: [-1, 0, 2, 1, 1, 0], sus2: [-1, 0, 2, 2, 0, 0],
  sus4: [-1, 0, 2, 2, 3, 0], m7b5: [-1, 0, 1, 0, 1, -1], aug: [-1, 0, 3, 2, 2, 1],
  dim: [-1, 0, 1, 2, 1, -1], dim7: [-1, 0, 1, 2, 1, -1],
};

const MINOR_SUFFIXES = new Set(["m", "min", "m7", "mM7", "m6", "dim", "dim7", "m7b5"]);

export function triadOf(label: string): string | null {
  const m = label.match(/^([A-G]#?)(.*)$/);
  if (!m) return null;
  return MINOR_SUFFIXES.has(m[2]) ? m[1] + "m" : m[1];
}

export interface ChordTones { bass: string; triad: string[] }
export function chordNotes(label: string): ChordTones | null {
  const t = triadOf(label);
  if (!t) return null;
  const m = t.match(/^([A-G]#?)(m?)$/);
  if (!m) return null;
  const root = NOTE_NAMES.indexOf(m[1]);
  const third = m[2] === "m" ? 3 : 4;
  const pc = (i: number) => NOTE_NAMES[(root + i) % 12];
  return { bass: `${m[1]}2`, triad: [`${pc(0)}3`, `${pc(third)}4`, `${pc(7)}4`] };
}

export interface ChordShape { label: string; frets: number[] }
export function chordShape(label: string): ChordShape | null {
  const m = label.match(/^([A-G]#?)(.*)$/);
  if (!m) return null;
  if (OPEN_CHORDS[label]) return { label, frets: OPEN_CHORDS[label] };
  const root = NOTE_NAMES.indexOf(m[1]);
  if (root < 0) return null;
  const q = m[2];
  const f6 = (((root - E_PC) % 12) + 12) % 12;
  const f5 = (((root - A_PC) % 12) + 12) % 12;
  const place = (tpl: number[], f: number) => tpl.map((o) => (o < 0 ? -1 : o + f));
  const cands: number[][] = [];
  if (ROOT6[q] !== undefined) cands.push(place(ROOT6[q], f6));
  if (ROOT5[q] !== undefined) cands.push(place(ROOT5[q], f5));
  if (cands.length === 0) {
    const tri = triadOf(label)!;
    if (OPEN_CHORDS[tri]) return { label, frets: OPEN_CHORDS[tri] };
    const minor = tri.endsWith("m");
    cands.push(place(minor ? ROOT6.m : ROOT6[""], f6));
    cands.push(place(minor ? ROOT5.m : ROOT5[""], f5));
  }
  const maxFret = (sh: number[]) => Math.max(...sh.filter((x) => x > 0), 0);
  cands.sort((x, y) => maxFret(x) - maxFret(y));
  return { label, frets: cands[0] };
}

// pitch classes of the song's lick (pentatonic) scale to highlight on the fretboard
export function scalePitchClasses(key: { tonic: string; quality: "major" | "minor" }): Set<number> {
  const tonic = NOTE_NAMES.indexOf(key.tonic);
  if (tonic < 0) return new Set();
  const minor = key.quality === "minor";
  const iv = minor ? [0, 3, 5, 7, 10] : [0, 2, 4, 7, 9];
  return new Set(iv.map((i) => (tonic + i) % 12));
}
